import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination,
  TextField, InputAdornment, Chip, IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Checkbox, FormControlLabel, CircularProgress, Alert, Stack,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import { useApi } from './useApi';
import { usePermissions } from './PermissionsProvider';
import { useAuth } from '../AuthProvider';

// Roles are keyed on the lower-cased UPN, which is what the access token carries as preferred_username.
const principalOf = (u) => (u.upn || u.email || '').trim().toLowerCase();

/** Who holds which role, with a dialog to change one person's roles. */
const UserRoles = () => {
  const api = useApi();
  const { refresh: refreshMyPermissions } = usePermissions();
  const { email: myEmail } = useAuth();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [assignments, setAssignments] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [onlyWithRoles, setOnlyWithRoles] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState([]);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    Promise.all([api('/api/v2/oms-users'), api('/api/v2/rbac/roles'), api('/api/v2/rbac/assignments')])
      .then(([userList, roleList, assignmentList]) => {
        setUsers(Array.isArray(userList) ? userList : []);
        setRoles(roleList);
        setAssignments(Object.fromEntries(assignmentList.map(a => [a.principal, a.roles])));
      })
      .catch(() => setError('Failed to load users and roles'))
      .finally(() => setLoading(false));
  }, [api]);

  const assignableRoles = roles.filter(r => !r.isDefault);
  const defaultRoleName = roles.find(r => r.isDefault)?.name || 'Employee';
  const roleName = code => roles.find(r => r.code === code)?.name || code;

  // Everyone in the OMS user list, plus anyone holding a role who has not been synced from Azure AD
  // yet, so those assignments stay visible and removable.
  const rows = useMemo(() => {
    const known = new Set(users.map(principalOf));
    const unsynced = Object.keys(assignments)
      .filter(p => !known.has(p))
      .map(p => ({ id: `unsynced:${p}`, displayName: p, email: p, unsynced: true, isActive: true }));
    return [...users, ...unsynced]
      .map(u => ({ ...u, principal: principalOf(u), roles: assignments[principalOf(u)] || [] }))
      .filter(u => u.principal)
      .filter(u => !onlyWithRoles || u.roles.length > 0)
      .filter(u => {
        const q = search.trim().toLowerCase();
        return !q || [u.displayName, u.email, u.department].some(v => (v || '').toLowerCase().includes(q));
      })
      .sort((a, b) => (b.roles.length > 0) - (a.roles.length > 0)
        || (a.displayName || '').localeCompare(b.displayName || ''));
  }, [users, assignments, search, onlyWithRoles]);

  const withRolesCount = Object.values(assignments).filter(r => r.length > 0).length;

  const openEditor = (row) => {
    setEditing(row);
    setDraft(row.roles);
  };

  const toggle = (code) => setDraft(prev => prev.includes(code) ? prev.filter(c => c !== code) : [...prev, code]);

  const save = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const saved = await api('/api/v2/rbac/assignments', 'PUT', { principal: editing.principal, roles: draft });
      setAssignments(prev => ({ ...prev, [saved.principal]: saved.roles }));
      setMessage({ severity: 'success', text: `Roles updated for ${editing.displayName}` });
      setEditing(null);
      if (saved.principal === (myEmail || '').toLowerCase()) refreshMyPermissions();
    } catch (e) {
      setMessage({
        severity: 'error',
        text: e.status === 409 ? 'You cannot remove your own Admin access' : 'Failed to update roles',
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} /></Box>;
  }

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Everyone is an <strong>{defaultRoleName}</strong> automatically. Give extra roles here; see
        Role Permissions for what each role unlocks. Changes apply within a minute.
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {message && <Alert severity={message.severity} sx={{ mb: 2 }} onClose={() => setMessage(null)}>{message.text}</Alert>}

      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <TextField
          size="small"
          placeholder="Search name, email, department…"
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(0); }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          sx={{ width: 320 }}
        />
        <Chip
          label={`With extra roles (${withRolesCount})`}
          color={onlyWithRoles ? 'primary' : 'default'}
          onClick={() => { setOnlyWithRoles(v => !v); setPage(0); }}
          sx={{ fontWeight: 700 }}
        />
      </Stack>

      <TableContainer sx={{ border: '1px solid #e2e8f0', borderRadius: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {['Name', 'Email', 'Department', 'Roles', ''].map(h => (
                <TableCell key={h} sx={{ fontWeight: 700, bgcolor: '#f8fafc' }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow><TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>No users match.</TableCell></TableRow>
            ) : rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map(u => (
              <TableRow key={u.id} hover sx={{ opacity: u.isActive ? 1 : 0.55 }}>
                <TableCell>
                  <Typography variant="body2" fontWeight={700}>{u.displayName}</Typography>
                  {u.unsynced && <Typography variant="caption" color="warning.main">Not synced from Azure AD yet</Typography>}
                  {!u.isActive && <Typography variant="caption" color="text.secondary">Left organisation</Typography>}
                </TableCell>
                <TableCell sx={{ color: 'text.secondary' }}>{u.email || '—'}</TableCell>
                <TableCell sx={{ color: 'text.secondary' }}>{u.department || '—'}</TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {u.roles.length === 0
                      ? <Typography variant="caption" color="text.secondary">{defaultRoleName} only</Typography>
                      : u.roles.map(code => <Chip key={code} size="small" label={roleName(code)} color="primary" variant="outlined" />)}
                  </Box>
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Edit roles">
                    <IconButton size="small" onClick={() => openEditor(u)} aria-label={`Edit roles for ${u.displayName}`}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={rows.length}
        page={page}
        onPageChange={(_, p) => setPage(p)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        rowsPerPageOptions={[25, 50, 100]}
      />

      <Dialog open={!!editing} onClose={() => setEditing(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800, pb: 1 }}>Roles for {editing?.displayName}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {editing?.principal} · Everyone also has the {defaultRoleName} role.
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {assignableRoles.map(r => (
              <FormControlLabel
                key={r.code}
                control={<Checkbox checked={draft.includes(r.code)} onChange={() => toggle(r.code)} />}
                label={
                  <Box>
                    <Typography variant="body2" fontWeight={700}>{r.name}</Typography>
                    <Typography variant="caption" color="text.secondary">{r.description}</Typography>
                  </Box>
                }
                sx={{ alignItems: 'flex-start', mb: 1, '& .MuiCheckbox-root': { pt: 0.5 } }}
              />
            ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setEditing(null)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={saving} sx={{ textTransform: 'none', fontWeight: 700 }}>
            {saving ? 'Saving…' : 'Save Roles'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserRoles;
